import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useRelatedCaseStudies } from "@/data/caseStudyRepo";

/**
 * RelatedCaseStudies — surfaces verified evidence for a service / market /
 * industry, sourced FIRST from the merged case-study repo (code-managed +
 * Admin-managed records) and, only if nothing matches there, falling back
 * to CMS-tagged /portfolio items.
 *
 * Supported filters (all optional):
 *   service   — matches caseStudy.services []
 *   market    — matches caseStudy.markets []
 *   industry  — matches caseStudy.industry (or industries[])
 *
 * Renders nothing when nothing matches (verified-only, per Rule 2).
 */
export default function RelatedCaseStudies({
  market,
  service,
  industry,
  limit = 3,
  kicker = "Related work",
  title = "Verified case studies",
}) {
  // 1) Merged case-study repo (Phase 4.0.1)
  const { items: caseStudies } = useRelatedCaseStudies({ service, industry, market, limit });

  // 2) Portfolio fallback (legacy CMS tagging) — only queried when there is
  //    no case-study match, to keep the surface deterministic and evidence-first.
  const [portfolio, setPortfolio] = useState([]);
  useEffect(() => {
    if (caseStudies.length > 0) {
      setPortfolio([]);
      return;
    }
    let mounted = true;
    api.get("/portfolio")
      .then((r) => {
        const all = Array.isArray(r.data) ? r.data : [];
        const matches = all.filter((it) => {
          if (market && !(it.markets || []).map((x) => (x || "").toLowerCase()).includes(String(market).toLowerCase())) return false;
          if (service && !(it.services || []).map((x) => (x || "").toLowerCase()).includes(String(service).toLowerCase())) return false;
          if (industry && !(it.industries || []).map((x) => (x || "").toLowerCase()).includes(String(industry).toLowerCase())) return false;
          return true;
        }).slice(0, limit);
        if (mounted) setPortfolio(matches);
      })
      .catch(() => { if (mounted) setPortfolio([]); });
    return () => { mounted = false; };
  }, [caseStudies.length, market, service, industry, limit]);

  const hasCaseStudies = caseStudies.length > 0;
  const hasPortfolio = portfolio.length > 0;
  if (!hasCaseStudies && !hasPortfolio) return null;

  return (
    <section className="py-14 md:py-20" data-testid="related-case-studies">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">{kicker}</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[1.1]">{title}</h2>
          </div>
          <Link
            to={hasCaseStudies ? "/case-studies" : "/portfolio"}
            className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
          >
            {hasCaseStudies ? "All case studies" : "All work"} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {hasCaseStudies
            ? caseStudies.map((cs) => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="group rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors"
                  data-testid={`related-case-${cs.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-muted/40">
                    {cs.heroImage && (
                      <img src={cs.heroImage} alt={cs.projectName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">
                      {cs.industry || cs.solutionType}
                    </div>
                    <div className="mt-1.5 font-heading font-bold text-lg leading-tight">{cs.projectName}</div>
                    {cs.shortDescription && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{cs.shortDescription}</p>}
                  </div>
                </Link>
              ))
            : portfolio.map((it) => (
                <Link
                  key={it.id}
                  to={`/portfolio/${it.slug}`}
                  className="group rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors"
                  data-testid={`related-case-${it.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden bg-muted/40">
                    {it.cover_image && (
                      <img src={it.cover_image} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground">{it.client || it.category_slug}</div>
                    <div className="mt-1.5 font-heading font-bold text-lg leading-tight">{it.title}</div>
                    {it.subtitle && <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{it.subtitle}</p>}
                  </div>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
}
